import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_ITEMS, LINKS } from "@/lib/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid="navbar"
      className="fixed inset-x-0 top-0 z-50"
    >
      <div
        className={`transition-all duration-300 ${
          scrolled
            ? "backdrop-blur-xl bg-white/80 border-b border-[#0B1F3A]/10 shadow-[0_8px_30px_rgba(11,31,58,0.05)]"
            : "bg-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="#home" data-testid="nav-logo" className="flex items-center gap-3">
            <img
              src="/assets/logo.jpeg"
              alt="Nexfold Consults"
              className="h-10 w-10 rounded-lg object-cover"
            />
            <div className="leading-none">
              <span
                className={`font-display text-lg font-extrabold tracking-tight transition-colors duration-300 ${
                  scrolled ? "text-[#0B1F3A]" : "text-white"
                }`}
              >
                Nexfold
              </span>
              <span
                className={`ml-1 font-display text-lg font-medium tracking-tight transition-colors duration-300 ${
                  scrolled ? "text-[#C8A75D]" : "text-[#C8A75D]"
                }`}
              >
                Consults
              </span>
            </div>
          </a>

          <div className="hidden md:flex items-center gap-9">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={`text-sm font-medium tracking-tight transition-colors duration-300 hover:text-[#C8A75D] ${
                  scrolled ? "text-[#1F2937]" : "text-white/85"
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="hidden md:block">
            <a
              href={LINKS.instagramDM}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-book-consultation-nav"
              className={`group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors duration-300 ${
                scrolled
                  ? "bg-[#0B1F3A] text-white hover:bg-[#C8A75D] hover:text-[#0B1F3A]"
                  : "bg-[#C8A75D] text-[#0B1F3A] hover:bg-white"
              }`}
            >
              Book a Consultation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>

          <button
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-open"
            aria-label="Open menu"
            className={`md:hidden ${scrolled ? "text-[#0B1F3A]" : "text-white"}`}
          >
            <Menu className="h-7 w-7" />
          </button>
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#0B1F3A] md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-20">
              <span className="font-display text-lg font-extrabold text-white">
                Nexfold <span className="text-[#C8A75D] font-medium">Consults</span>
              </span>
              <button onClick={() => setOpen(false)} data-testid="nav-mobile-close" aria-label="Close menu">
                <X className="h-7 w-7 text-white" />
              </button>
            </div>
            <div className="flex flex-col gap-2 px-6 pt-8">
              {NAV_ITEMS.map((item, i) => (
                <motion.a
                  key={item.label}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i }}
                  className="font-display text-3xl font-bold text-white py-3 border-b border-white/10"
                >
                  {item.label}
                </motion.a>
              ))}
              <a
                href={LINKS.instagramDM}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="cta-book-consultation-mobile"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#C8A75D] px-6 py-4 text-base font-semibold text-[#0B1F3A]"
              >
                Book a Consultation <ArrowUpRight className="h-5 w-5" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
